import { useEffect, useState } from 'react';
import { Button, Badge, Card } from '@atoms/index';
import { restaurantOrderService, type AssignableCourier } from '@services/restaurantOrderService';
import { cn } from '@/utils/cn';

interface AssignCourierModalProps {
  orderId: string;
  orderNumber?: string;
  isOpen: boolean;
  onClose: () => void;
  onAssigned: () => void;
}

export function AssignCourierModal({
  orderId,
  orderNumber,
  isOpen,
  onClose,
  onAssigned,
}: AssignCourierModalProps) {
  const [couriers, setCouriers] = useState<AssignableCourier[]>([]);
  const [deliveryProviderType, setDeliveryProviderType] = useState<
    'PLATFORM' | 'OWN_FLEET' | 'HYBRID' | null
  >(null);
  const [selectedCourierId, setSelectedCourierId] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !isSubmitting) {
        onClose();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isOpen, isSubmitting, onClose]);

  useEffect(() => {
    if (!isOpen) {
      setSelectedCourierId('');
      setError(null);
      setDeliveryProviderType(null);
      return;
    }

    setIsLoading(true);
    setError(null);

    restaurantOrderService
      .getAssignableCouriers(orderId)
      .then((result) => {
        setCouriers(result.couriers);
        setDeliveryProviderType(result.deliveryProviderType);
        if (result.couriers.length === 1) {
          setSelectedCourierId(result.couriers[0].id);
        }
      })
      .catch((err) => {
        const apiMessage = err?.response?.data?.error?.message as string | undefined;
        const status = err?.response?.status;

        let message = apiMessage;
        if (status === 404) {
          message =
            'Kurye atama servisi şu anda kullanılamıyor. API güncellemesi gerekiyor; lütfen destek ekibiyle iletişime geçin.';
        } else if (!message) {
          message = 'Müsait kuryeler yüklenirken bir hata oluştu.';
        }

        setError(message);
        setCouriers([]);
        setDeliveryProviderType(null);
      })
      .finally(() => setIsLoading(false));
  }, [isOpen, orderId]);

  const handleAssign = async () => {
    if (!selectedCourierId) {
      setError('Lütfen bir kurye seçin.');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      await restaurantOrderService.assignCourier(orderId, selectedCourierId);
      onAssigned();
      onClose();
    } catch (err: any) {
      setError(
        err?.response?.data?.error?.message ||
          'Kurye ataması yapılamadı. Lütfen tekrar deneyin.',
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) {
    return null;
  }

  const emptyStateHint =
    deliveryProviderType === 'PLATFORM'
      ? 'Platform teslimat modelinde yalnızca platform kuryeleri listelenir. Restoran kuryeleri bu siparişe atanamaz.'
      : deliveryProviderType === 'OWN_FLEET'
        ? 'Kendi filo modelinde yalnızca restoranınıza bağlı kuryeler listelenir.'
        : 'Hibrit modelde restoran filosu ve platform kuryeleri listelenir.';

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center px-4">
      <button
        className="absolute inset-0 bg-black/50"
        onClick={onClose}
        aria-label="Modal kapat"
        disabled={isSubmitting}
      />
      <Card className="relative z-[10000] w-full max-w-lg p-0 overflow-hidden shadow-2xl">
        <div className="p-6">
          <h3 className="text-xl font-bold text-gray-900 mb-1">Kurye Ata</h3>
          <p className="text-sm text-gray-600 mb-5">
            {orderNumber
              ? `#${orderNumber} numaralı sipariş için müsait kuryelerden birini seçin.`
              : 'Sipariş için müsait kuryelerden birini seçin.'}
          </p>

          {isLoading ? (
            <div className="py-10 text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-3" />
              <p className="text-sm text-gray-600">Müsait kuryeler yükleniyor...</p>
            </div>
          ) : error ? (
            <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 mb-4">
              {error}
            </div>
          ) : couriers.length === 0 ? (
            <div className="rounded-xl border border-dashed border-gray-200 bg-gray-50 px-4 py-6 text-center mb-4">
              <p className="text-sm text-gray-600">
                Şu anda atanabilecek müsait kurye bulunmuyor.
              </p>
              <p className="text-xs text-gray-500 mt-1">{emptyStateHint}</p>
              <p className="text-xs text-gray-500 mt-1">
                Kuryelerin admin tarafından aktif edilmiş, mesaide (çevrimiçi) ve kapasitesi uygun olduğundan emin olun.
              </p>
            </div>
          ) : (
            <div className="space-y-2 max-h-72 overflow-y-auto pr-1 mb-4">
              {couriers.map((courier) => {
                const isSelected = selectedCourierId === courier.id;
                return (
                  <button
                    key={courier.id}
                    type="button"
                    onClick={() => setSelectedCourierId(courier.id)}
                    className={cn(
                      'w-full text-left rounded-2xl border p-4 transition-all',
                      isSelected
                        ? 'border-primary bg-primary/5 shadow-sm'
                        : 'border-gray-200 hover:border-primary/30 hover:bg-gray-50',
                    )}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <p className="font-semibold text-gray-900">
                          {courier.name || 'Kurye'}
                        </p>
                        {courier.phone && (
                          <p className="text-sm text-gray-600 mt-0.5">{courier.phone}</p>
                        )}
                        <div className="flex flex-wrap items-center gap-2 mt-2 text-xs text-gray-500">
                          {courier.vehicleType && <span>{courier.vehicleType}</span>}
                          {courier.plateNumber && <span>• {courier.plateNumber}</span>}
                          <span>
                            • Kapasite {courier.activeOrdersCount}/{courier.maxCapacity}
                          </span>
                        </div>
                      </div>
                      <div className="flex flex-col items-end gap-2 shrink-0">
                        <Badge
                          variant={courier.status === 'ONLINE' ? 'success' : 'warning'}
                          size="sm"
                        >
                          {courier.status === 'ONLINE' ? 'Müsait' : courier.status}
                        </Badge>
                        <Badge variant="secondary" size="sm">
                          {courier.courierType === 'RESTAURANT' ? 'Restoran' : 'Platform'}
                        </Badge>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          )}

          <div className="flex gap-3">
            <Button
              variant="secondary"
              className="w-full"
              onClick={onClose}
              disabled={isSubmitting}
            >
              Vazgeç
            </Button>
            {!error && (
              <Button
                variant="primary"
                className="w-full"
                onClick={handleAssign}
                disabled={
                  isSubmitting || isLoading || couriers.length === 0 || !selectedCourierId
                }
                isLoading={isSubmitting}
              >
                Kuryeyi Ata
              </Button>
            )}
          </div>
        </div>
      </Card>
    </div>
  );
}

export default AssignCourierModal;
